import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { brand, textures } from '../theme';
import Img from '../components/Img';
import { gsap, useGsap, QUERIES } from '../animation/gsap';
import { useGallery } from '../content/ContentProvider';
import { featuredPhotos } from '../api/content';
import PhotoCard from '../components/PhotoCard';

/**
 * Recent work, as a full-screen reel.
 *
 * The section pins and the featured photos cross-fade through one another as
 * you scroll, each one drifting slowly closer, with its caption sliding in
 * underneath. It is the closest thing the site has to a showreel.
 *
 * Off the desktop — or with reduced motion on — it falls back to a plain grid
 * of the same photos with their captions, which is the same information without
 * the theatre.
 */
/*
 * One spacing value for the whole card grid.
 *
 * It sets the gutters between the cards AND the space above the "See the full
 * gallery" button, so the gap over the button is exactly the gap between the
 * rows. Defined once because the two only look right while they match, and two
 * separate values would drift the first time either is touched.
 */
const GRID_GAP = { xs: 2, sm: 3 };

export default function GalleryReel() {
	const gallery = useGallery();
	const photos = featuredPhotos(gallery, 6);

	const root = useGsap(
		({ mm }) => {
			const q = gsap.utils.selector(root);

			mm.add(QUERIES.desktop, () => {
				const slides = q('[data-slide]');
				const captions = q('[data-caption]');
				const counter = q('[data-counter]')[0];

				if (slides.length < 2) {
					return undefined;
				}

				/*
				 * Switch from the grid to the reel.
				 *
				 * The reel stacks all five photos and all five captions on top of
				 * one another and relies on the code below to show one at a time.
				 * If that code did not run — reduced motion, or a bundle that
				 * failed to load — five captions would print on top of each other.
				 * So the grid is the default and the reel is only laid out once the
				 * thing that drives it is definitely running.
				 */
				root.current.dataset.cinematic = 'true';

				// Stack them. Only the first is visible to begin with.
				gsap.set(slides, { autoAlpha: 0, scale: 1.18 });
				gsap.set(slides[0], { autoAlpha: 1, scale: 1 });
				gsap.set(captions, { autoAlpha: 0, y: 24 });
				gsap.set(captions[0], { autoAlpha: 1, y: 0 });

				const timeline = gsap.timeline({
					scrollTrigger: {
						trigger: root.current,
						start: 'top top',
						// A screen of scroll per photo after the first.
						end: () => `+=${(slides.length - 1) * 90}%`,
						scrub: 1,
						pin: true,
						anticipatePin: 1,
						invalidateOnRefresh: true,
						onUpdate: (self) => {
							if (!counter) {
								return;
							}

							/*
							 * Flip the number at the midpoint of each crossfade.
							 *
							 * The timeline runs one unit of time per photo after the
							 * first, and the fade to photo i occupies [i-1, i-0.5] of
							 * it. The old arithmetic divided progress into equal
							 * fifths instead, so the photo finished fading well
							 * before the number caught up. Adding 0.75 to the
							 * timeline position makes the counter tick over exactly
							 * halfway through the fade, which is when the eye reads
							 * the picture as having changed.
							 */
							const time = self.progress * (slides.length - 1);
							const index = Math.min(slides.length - 1, Math.floor(time + 0.75));

							counter.textContent = String(index + 1).padStart(2, '0');
						},
					},
				});

				slides.forEach((slide, index) => {
					if (index === 0) {
						return;
					}

					const position = index - 1;

					timeline
						.to(slides[index - 1], { autoAlpha: 0, scale: 0.95, ease: 'none' }, position)
						.to(slide, { autoAlpha: 1, scale: 1, ease: 'none' }, position)
						.to(captions[index - 1], { autoAlpha: 0, y: -20, ease: 'none' }, position)
						.to(captions[index], { autoAlpha: 1, y: 0, ease: 'none' }, position);
				});

				// Back to the grid if reduced motion is switched on, or on unmount.
				return () => {
					if (root.current) {
						delete root.current.dataset.cinematic;
					}
				};
			});

			mm.add(QUERIES.mobile, () => {
				/*
				 * The three flags below are not optional decoration — without them
				 * this tween leaves the cards displaced on top of the button.
				 *
				 * A from() tween renders its starting state the moment it is created,
				 * so the cards sat at translateY(30px) from page load and only
				 * returned to 0 once the trigger fired. Any interruption — a revert,
				 * a refresh, React remounting the section — left them stuck 30px
				 * below their layout box, overlapping whatever follows. A transform
				 * moves what you see but not what the grid measures, so no amount of
				 * spacing below the grid can fix it.
				 *
				 *   immediateRender: false — stay at the natural position until the
				 *     trigger actually fires, so nothing is displaced before then.
				 *   clearProps: 'transform' — remove the inline transform on
				 *     completion, so nothing is displaced after, either.
				 *   once: true — the reveal is a one-off; re-running it on every
				 *     scroll back is what re-applies the start state.
				 */
				gsap.from(q('[data-grid-item]'), {
					autoAlpha: 0,
					y: 30,
					duration: 0.6,
					stagger: 0.1,
					ease: 'power2.out',
					immediateRender: false,
					clearProps: 'transform',
					scrollTrigger: { trigger: root.current, start: 'top 75%', once: true },
				});
			});
		},
		[photos.length]
	);

	return (
		<Box
			ref={root}
			component="section"
			sx={{
				position: 'relative',
				/*
				 * The one mustard section. Every other band on the page is white,
				 * warm grey, or charcoal, so the brand gold at full strength makes
				 * this the screen people remember — and it belongs on the section
				 * that shows the actual work.
				 */
				backgroundColor: brand.accent,
				color: brand.darkDeep,
				overflow: 'hidden',
				py: 8,

				'&[data-cinematic="true"]': {
					minHeight: '100vh',
					py: 0,
					display: 'flex',
					alignItems: 'center',
				},
			}}
		>
			{/* Grout lines over the gold, barely there, so the band reads as a
			    tiled surface rather than a flat block of colour. */}
			<Box
				aria-hidden="true"
				sx={{
					position: 'absolute',
					inset: 0,
					...textures.groutGrid('rgb(31 31 31 / 7%)', '4.5rem'),
					maskImage: 'radial-gradient(ellipse at 20% 30%, #000 0%, transparent 80%)',
					pointerEvents: 'none',
				}}
			/>
			{/* ---- The pinned reel. Hidden until GSAP opts into it. ---- */}
			<Box sx={{ display: 'none', width: '100%', '[data-cinematic="true"] &': { display: 'block' } }}>
				<Container>
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: '1fr 1.15fr',
							gap: 8,
							alignItems: 'center',
						}}
					>
						<Box>
							<Typography variant="overline" sx={{ display: 'block', color: brand.darkDeep, mb: 2 }}>
								Recent work
							</Typography>

							<Typography variant="h2" component="h2" sx={{ color: brand.white, mb: 3, textShadow: '0 1px 2px rgb(31 31 31 / 18%)' }}>
								Rooms we have finished
							</Typography>

							{/* The captions sit stacked, and the scrub swaps them. */}
							<Box sx={{ position: 'relative', minHeight: '9rem' }}>
								{photos.map((photo) => (
									<Typography
										key={photo.file}
										data-caption
										sx={{
											position: 'absolute',
											inset: 0,
											color: 'rgb(31 31 31 / 78%)',
											fontSize: '1.05rem',
											maxWidth: '46ch',
										}}
									>
										{photo.caption}
									</Typography>
								))}
							</Box>

							<Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 2, mb: 4 }}>
								<Typography
									data-counter
									sx={{ color: brand.darkDeep, fontSize: '2.5rem', fontWeight: 700, lineHeight: 1 }}
								>
									01
								</Typography>
								<Typography sx={{ color: 'rgb(31 31 31 / 55%)', fontSize: '1rem' }}>
									/ {String(photos.length).padStart(2, '0')}
								</Typography>
							</Box>

							{/* Block-level wrapper carries the gap, for the same reason as
							    the grid version below. */}
							<Box sx={{ mt: 4 }}>
								<Button variant="contained" color="secondary" component={RouterLink} to="/gallery">
									See the full gallery
								</Button>
							</Box>
						</Box>

						<Box
							sx={{
								position: 'relative',
								aspectRatio: '4 / 5',
								borderRadius: 2,
								overflow: 'hidden',
								boxShadow: '0 30px 80px rgb(0 0 0 / 45%)',
							}}
						>
							{photos.map((photo) => (
								<Img
									key={photo.file}
									data-slide
									src={photo.src}
									srcSet={photo.srcset}
									sizes="(min-width: 62rem) 34rem, 100vw"
									alt={photo.alt}
									width={photo.width}
									height={photo.height}
									loading="lazy"
									sx={{
										position: 'absolute',
										inset: 0,
										width: '100%',
										height: '100%',
										objectFit: 'cover',
										display: 'block',
									}}
								/>
							))}
						</Box>
					</Box>
				</Container>
			</Box>

			{/* ---- The default: a plain grid. What phones get, and what anyone
			     gets when the reel is not running. ---- */}
			<Container sx={{ display: 'block', '[data-cinematic="true"] &': { display: 'none' } }}>
				<Typography variant="overline" sx={{ display: 'block', color: brand.darkDeep, mb: 2 }}>
					Recent work
				</Typography>
				<Typography variant="h2" component="h2" sx={{ color: brand.white, mb: 5, textShadow: '0 1px 2px rgb(31 31 31 / 18%)' }}>
					Rooms we have finished
				</Typography>

				{/*
					Two across on phones, three on tablets — compact cards, not a column
					of screen-filling photos. Six photos fill both layouts exactly.
				*/}
				<Box
					sx={{
						display: 'grid',
						gap: GRID_GAP,
						gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
					}}
				>
					{photos.map((photo) => (
						<PhotoCard
							key={photo.file}
							photo={photo}
							data-grid-item
							sizes="(min-width: 36rem) 33vw, 50vw"
							// Same card as the gallery page, cropped to one window so the
							// grid rows stay even with mixed portrait and landscape shots.
							imageSx={{ aspectRatio: '4 / 3', objectFit: 'cover' }}
							captionSx={{ p: { xs: 1.5, sm: 2 }, fontSize: { xs: '0.78rem', sm: '0.9rem' } }}
						/>
					))}
				</Box>

				{/*
					The gap and the centring both live on this wrapper, not on the
					button, because a MUI Button is display: inline-flex and neither
					behaves predictably on an inline-level box.

					The gap is padding rather than margin on purpose. A margin here
					kept collapsing away to almost nothing against the grid above, even
					though the rule was generated correctly. Padding cannot collapse, so
					the space is simply always there — and it is GRID_GAP, so it is the
					same gap the card rows have between them.

					Left-aligned again from lg up, where this grid is only ever seen by
					someone with reduced motion switched on; there it should match the
					left-aligned button in the reel it stands in for.
				*/}
				<Box sx={{ pt: GRID_GAP, textAlign: { xs: 'center', lg: 'left' } }}>
					<Button variant="contained" color="secondary" component={RouterLink} to="/gallery">
						See the full gallery
					</Button>
				</Box>
			</Container>
		</Box>
	);
}
